import React, { useState, useEffect, useContext } from "react"
import { Button, makeStyles } from '@material-ui/core'
import { Box, Typography, TextField, Grid, Backdrop, CircularProgress, Select, MenuItem, FormControl, InputLabel, Alert } from '@mui/material'
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableContainer,
    TableRow,
    TableBody,
    TableCell,
    Paper
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TitleHeader from "../components/TitleHeader"
import UserContext from "../context/UserContext"
import TerminalInfoTable from "../components/TerminalInfoTable"
import TerminalInfoPagination from "../components/TerminalInfoPagination"
import { generateClient } from 'aws-amplify/api'
import * as queries from '../graphql/queries'
import * as mutations from '../graphql/mutations'
import { getScriptPath, getActionStatus, findActionByScriptPath } from "../utils/scriptActionMappings"
import { useScriptMappings } from "../hooks/useScriptMappings"

const useStyles = makeStyles({
    root: {
        width: '100%',
        flex: 1,
        alignItems: 'center',
        alignContent: 'center',
        justifyContent: 'center'
    },
    container: {
        width: '90%',
        margin: '0 auto'
    },
    title: {
        marginBlock: '2%',
        marginBottom: '5%'
    },
    accordionContainer: {
        marginTop: '2%'
    },
    customAccordionSummary: {
        justifyContent: "flex-start"
    },
    textField: {
        marginBlock: '1%',
        align: 'left'
    },
    btnContainer: {
        marginTop: '1%',
        marginBottom: '1%',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start'
    },
    btn: {
        marginRight: '1%'
    },
    cell: {
        borderRight: "0.5px solid #DEDEDE"
    }
})

/**
 * 端末設定ページ
 * @component
 */
const RouterSettingPage = (props) => {
    const classes = useStyles()
    const userContext = useContext(UserContext)
    const [userGroup, setUserGroup] = useState('')

    const { history } = props
    const {checkedMACList, mac} = props.location
    const [dispMac, setDispMac] = useState(mac)
    const [terminalInfo, setTerminalInfo] = useState([])
    const [config, setConfig] = useState({})
    const [log, setLog] = useState("")
    const [terminalGroupId, setTerminalGroupId] = useState(userGroup)

    const client = generateClient()

    useEffect(() => {
        const fetchUserGroup = async () => {
            const groups = await userContext.getCurrentUserGroup()
            if (groups && groups.length > 0) {
                setUserGroup(groups[0])
                setTerminalGroupId(groups[0])
            }
        }
        fetchUserGroup()
    }, [userContext])
    const [primaryKeyList, setPrimarykeyList] = useState([])
    const [scriptAction, setScriptAction] = useState("")
    const [scriptFileName, setScriptFileName] = useState("")
    const [open, setOpen] = useState(false)

    const [nextToken, setNextToken] = useState(undefined)
    const [nextNextToken, setNextNextToken] = useState()
    const [previousTokens, setPreviousTokens] = useState([])

    // Fetch script action mappings from DynamoDB (only source)
    const { mappings: scriptActions, loading: mappingsLoading, error: mappingsError } = useScriptMappings()

    const isSingleTerminalSettingMode = (checkedMACList === void 0) // チェックリストが渡ってきていない場合は単一設定画面とする
    useEffect(() => {
        /**
         * MACアドレスから端末情報を取得する。
         * @param {string} mac  
         */
        const getManagementTerminal = async(mac) => {
            try {
                // const response = await client.graphql({ query: queries.listTerminalManagementScreenInfosByMac, variables: {MAC: mac, terminalInfoLimit: 1000, terminalInfoOrder: 8700}})
                const MAX_ONE_QUERY_LIMIT = 100
                setOpen(true)
                // const response = await client.graphql({ query: queries.listTerminalManagementScreenInfosByMac, variables: {MAC: mac, terminalInfoLimit: MAX_ONE_QUERY_LIMIT, terminalInfoNextToken: nextToken[terminalInfoPage]}})
                const response = await client.graphql({ query: queries.listTerminalManagementScreenInfosByMac, variables: {MAC: mac, terminalInfoLimit: MAX_ONE_QUERY_LIMIT, terminalInfoNextToken: nextToken}})
                const terminalSettingScreenItem = response.data.registeredTerminalByMac.items
                if(terminalSettingScreenItem.length) {
                    const resTerminalInfo = terminalSettingScreenItem[0].TerminalInfo
                    // loop though resTerminalInfo.items and set Action and ScriptPath of terminalSettingScreenItem[0] to it
                    for (let i = 0; i < resTerminalInfo.items.length; i++) {
                      resTerminalInfo.items[i].Action =
                        terminalSettingScreenItem[0].Action;
                      resTerminalInfo.items[i].Script =
                        terminalSettingScreenItem[0].ScriptPath;
                    }


                    // ページングされている（1クエリの取得容量制限もしくは元レコードがMAX_ONE_QUERY_LIMIT件以上）
                    if(resTerminalInfo.nextToken) {
                        if(MAX_ONE_QUERY_LIMIT > resTerminalInfo.items.length) {
                            const limit = MAX_ONE_QUERY_LIMIT - resTerminalInfo.items.length
                            const nextTerminalInfo = await getNextTerminalInfo(resTerminalInfo.nextToken, mac, limit)
                            setTerminalInfo([...resTerminalInfo.items, ...nextTerminalInfo])

                        } else {
                            setTerminalInfo(resTerminalInfo.items)
                            setNextNextToken(resTerminalInfo.nextToken)
                        }
                    } else {
                        setTerminalInfo(resTerminalInfo.items)
                    }
                    // Configの設定
                    const configObj = JSON.parse(terminalSettingScreenItem[0].Config)
                    setConfig(configObj)
                    setLog(terminalSettingScreenItem[0].Log)
                    setTerminalGroupId(terminalSettingScreenItem[0].GroupId)
                    // Set action based on the database value
                    const dbAction = terminalSettingScreenItem[0].Action
                    const dbScriptPath = terminalSettingScreenItem[0].ScriptPath
                    if (dbAction && dbAction !== false && scriptActions) {
                        // Find the action key that matches the script path
                        const foundAction = findActionByScriptPath(dbScriptPath, scriptActions)
                        setScriptAction(foundAction)
                        // Set script file name (will be used if action needs user input)
                        setScriptFileName(dbScriptPath || "")
                    } else {
                        setScriptAction("None")
                        setScriptFileName("")
                    }
                }
            } catch (e) {
                console.log(e)
                history.goBack()
            }
            setOpen(false)
        }

        /**
         * 複数MACアドレスから端末情報を取得する。
         * @param {[string]} macList
         */
        const getManagementTerminalList = async() => {
            const response = await Promise.all(checkedMACList.map(async mac => {
                try {
                    const response = await client.graphql({ query: queries.listTerminalManagementScreenInfosByMac, variables: {MAC: mac}})
                    const terminalSettingScreenItem = response.data.registeredTerminalByMac.items
                    if(terminalSettingScreenItem.length) {
                        const pkeyList = {
                            MAC: mac,
                            GroupId: terminalSettingScreenItem[0].GroupId
                        }
                        return pkeyList
                    }
                } catch (e) {
                    console.log(e)
                    history.goBack()
                }
            }))
            setPrimarykeyList(response)
        }
        

        if(isSingleTerminalSettingMode) {
            getManagementTerminal(dispMac)
        } else {
            getManagementTerminalList()
        }
    }, [nextToken])

    const getNextTerminalInfo = async(nextTerminalInfoToken, mac, nextLimit) => {
        let token = nextTerminalInfoToken
        let limit = nextLimit
        const retTerminalInfoList = []
        do {
            try {
                const response = await client.graphql({ query: queries.listTerminalInfos, variables: {nextToken: token, limit: limit, MAC: mac, sortDirection: "DESC"}})
                const termnalLatestData = response.data.listTerminalInfos.items
                token = response.data.listTerminalInfos.nextToken
                retTerminalInfoList.push(...termnalLatestData)
                limit = limit - retTerminalInfoList.length
                if(limit <= 0) {
                    break
                }
            } catch (e) {
                console.log(e)
                token = null
            }
        } while (token)
        console.log(retTerminalInfoList.length)
        console.log(retTerminalInfoList.slice(-1)[0])
        
        setNextNextToken(token)
        return retTerminalInfoList
    }

    const onClickBackBtn = () => {
        history.goBack()
    }

    /**
     * Reload terminal data after update
     */
    const reloadTerminalData = async() => {
        try {
            setOpen(true)
            const MAX_ONE_QUERY_LIMIT = 100
            const response = await client.graphql({ query: queries.listTerminalManagementScreenInfosByMac, variables: {MAC: dispMac, terminalInfoLimit: MAX_ONE_QUERY_LIMIT, terminalInfoNextToken: nextToken}})
            const terminalSettingScreenItem = response.data.registeredTerminalByMac.items
            if(terminalSettingScreenItem.length) {
                const resTerminalInfo = terminalSettingScreenItem[0].TerminalInfo
                // loop though resTerminalInfo.items and set Action and ScriptPath of terminalSettingScreenItem[0] to it
                for (let i = 0; i < resTerminalInfo.items.length; i++) {
                    resTerminalInfo.items[i].Action = terminalSettingScreenItem[0].Action
                    resTerminalInfo.items[i].Script = terminalSettingScreenItem[0].ScriptPath;
                }

                // ページングされている（1クエリの取得容量制限もしくは元レコードがMAX_ONE_QUERY_LIMIT件以上）
                if(resTerminalInfo.nextToken) {
                    if(MAX_ONE_QUERY_LIMIT > resTerminalInfo.items.length) {
                        const limit = MAX_ONE_QUERY_LIMIT - resTerminalInfo.items.length
                        const nextTerminalInfo = await getNextTerminalInfo(resTerminalInfo.nextToken, dispMac, limit)
                        setTerminalInfo([...resTerminalInfo.items, ...nextTerminalInfo])
                    } else {
                        setTerminalInfo(resTerminalInfo.items)
                        setNextNextToken(resTerminalInfo.nextToken)
                    }
                } else {
                    setTerminalInfo(resTerminalInfo.items)
                }
                // Configの設定
                const configObj = JSON.parse(terminalSettingScreenItem[0].Config)
                setConfig(configObj)
                setLog(terminalSettingScreenItem[0].Log)
                setTerminalGroupId(terminalSettingScreenItem[0].GroupId)
                // Set action based on the database value
                const dbAction = terminalSettingScreenItem[0].Action
                const dbScriptPath = terminalSettingScreenItem[0].ScriptPath
                if (dbAction && dbAction !== false && scriptActions) {
                    // Find the action key that matches the script path
                    const foundAction = findActionByScriptPath(dbScriptPath, scriptActions)
                    setScriptAction(foundAction)
                    // Set script file name (will be used if action needs user input)
                    setScriptFileName(dbScriptPath || "")
                } else {
                    setScriptAction("None")
                    setScriptFileName("")
                }
            }
        } catch (e) {
            console.log(e)
        } finally {
            setOpen(false)
        }
    }

    const onClickUpdateBtn = async() => {
        if (!scriptActions) {
            console.error('Script actions not loaded from database');
            return;
        }

        // Determine Action column value based on selection
        const actionValue = getActionStatus(scriptAction)
        const actionData = scriptActions[scriptAction]
        const scriptPathValue = getScriptPath(scriptAction, actionData, scriptFileName)

        if(isSingleTerminalSettingMode) {
            const updateInfo = {
                MAC: dispMac,
                Action: actionValue,
                ScriptPath: scriptPathValue,
                GroupId: terminalGroupId
            }
            try {
                await client.graphql({ query: mutations.updateTerminalAction, variables: {input: updateInfo}})
                // Reload data after successful update
                await reloadTerminalData()
            }catch (e) {
                console.log(e)
            }
        } else {
            await Promise.all(primaryKeyList.map(async primaryKey => {
                const updateInfo = {
                    MAC: primaryKey.MAC,
                    Action: actionValue,
                    ScriptPath: scriptPathValue,
                    GroupId: primaryKey.GroupId
                }
                try {
                    await client.graphql({ query: mutations.updateTerminalAction, variables: {input: updateInfo}})
                } catch (e) {
                    console.log(e)
                }
            }))
        }
    }

    const onClickDeleteBtn = async() => {
        if(isSingleTerminalSettingMode) {
            const updateInfo = {
                MAC: dispMac,
                GroupId: terminalGroupId,
                Model: null,
                Firmware: null,
                Action: false,
                ScriptPath: null,
                Uptime: null,
                WanIp: null,
                CellularModule: null,
                IMEI: null,
                MSISDN: null,
                NetType: null,
                RSSI: null,
                LTERSRP: null,
                NRRSRP: null,
                MCC: null,
                MNC: null,
                Result: "NONE",
                Time: null,
                LastUpdated: null,
                Config: null
            }
            try {
                await client.graphql({ query: mutations.updateTerminalAction, variables: {input: updateInfo}})
                // Clear UI after successful delete
                setScriptAction("None")
                setScriptFileName("")
            }catch (e) {
                console.log(e)
            }
        } else {
            await Promise.all(primaryKeyList.map(async primaryKey => {
                const updateInfo = {
                    MAC: primaryKey.MAC,
                    GroupId: primaryKey.GroupId,
                    Model: null,
                    Firmware: null,
                    Action: false,
                    ScriptPath: null,
                    Uptime: null,
                    WanIp: null,
                    CellularModule: null,
                    IMEI: null,
                    MSISDN: null,
                    NetType: null,
                    RSSI: null,
                    LTERSRP: null,
                    NRRSRP: null,
                    MCC: null,
                    MNC: null,
                    Result: "NONE",
                    Time: null,
                    LastUpdated: null,
                    Config: null
                }
                try {
                    await client.graphql({ query: mutations.updateTerminalAction, variables: {input: updateInfo}})
                } catch (e) {
                    console.log(e)
                }
            }))
            // Clear UI after successful delete
            setScriptAction("None")
            setScriptFileName("")
        }
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.container}>
                <TitleHeader  history={props.history}/>
                <Box className={classes.accordionContainer}>
                    {/* Settings */}
                    <Accordion>
                        <AccordionSummary
                            classes={{ content: classes.customAccordionSummary}}
                            style={{width: '100%', padding: '0 16px'}}
                            expandIcon={<ExpandMoreIcon/>}
                        >
                            <Typography variant="h5">Settings</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {mappingsError && (
                                <Grid container spacing={2} style={{ width: '100%' }}>
                                    <Grid item xs={12}>
                                        <Alert severity="error">
                                            Failed to load script actions from database: {mappingsError.message}
                                        </Alert>
                                    </Grid>
                                </Grid>
                            )}

                            {mappingsLoading && (
                                <Grid container spacing={2} style={{ width: '100%' }}>
                                    <Grid item xs={12}>
                                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <CircularProgress size={24} style={{ marginRight: '10px' }} />
                                            <Typography>Loading script actions...</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}

                            {!mappingsLoading && !mappingsError && scriptActions && (
                                <Grid container spacing={2}>
                                    { isSingleTerminalSettingMode ?
                                        <Grid
                                            item
                                            xs={8} >
                                            <Typography variant='h6' align='left'>MAC: {dispMac}</Typography>
                                        </Grid>
                                        : null
                                    }
                                    <Grid
                                        item
                                        xs={8} >
                                        <TextField
                                            select
                                            fullWidth
                                            size='small'
                                            label="Action"
                                            value={scriptAction}
                                            onChange={(e) => setScriptAction(e.target.value)}
                                            style={{
                                                textAlign: 'left'
                                            }}
                                        >
                                            {Object.keys(scriptActions).map((action) => (
                                            <MenuItem key={action} value={action} style={{
                                                display: 'flex', padding: '6px 16px', justifyContent: 'flex-start'
                                            }}>
                                                {action}
                                            </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    {scriptActions[scriptAction]?.needsUserInput && (
                                        <Grid
                                            item
                                            xs={8} >
                                            <TextField
                                                id="script-filename-input"
                                                label="Script file name"
                                                variant="outlined"
                                                size='small'
                                                fullWidth
                                                className={classes.textField}
                                                value={scriptFileName}
                                                onChange={(e) => setScriptFileName(e.target.value)}
                                                helperText="Enter the script path or file name"
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            )}

                            <Box className={classes.btnContainer}>
                                <Button
                                    className={classes.btn}
                                    variant="contained"
                                    color='primary'
                                    onClick={onClickUpdateBtn}
                                    disabled={mappingsLoading || mappingsError || !scriptActions}
                                >
                                    Update the Record
                                </Button>
                                <Button
                                    className={classes.btn}
                                    variant="contained"
                                    color='secondary'
                                    onClick={onClickDeleteBtn}
                                    disabled={mappingsLoading || mappingsError || !scriptActions}
                                >
                                    Delete the Record
                                </Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    { isSingleTerminalSettingMode &&
                        <>
                            {/* Config */}
                            <Accordion>
                                <AccordionSummary
                                    style={{width: '100%', padding: '0 16px'}}
                                    expandIcon={<ExpandMoreIcon/>}
                                >
                                    <Typography variant="h5">Config</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TableContainer component={Paper}>
                                        <Table sx={{ minWidth: 650 }} aria-label="a dense table">
                                            <TableBody>
                                                { config != null && Object.keys(config).map((key) => (
                                                    <TableRow
                                                        key={key}
                                                    >
                                                        <TableCell component="th" scope="row" style={{
                                                            borderRight: "0.5px solid #DEDEDE"
                                                            }}>{key}</TableCell>
                                                        <TableCell align="left">{config[key]}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
        
                            {/* Terminal Info  */}
                            <Accordion>
                                <AccordionSummary
                                    style={{width: '100%', padding: '0 16px'}}
                                    expandIcon={<ExpandMoreIcon/>}
                                >
                                    <Typography variant="h5">Info</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TerminalInfoPagination
                                        setNextToken={setNextToken}
                                        nextNextToken={nextNextToken}
                                        previousTokens={previousTokens} 
                                        setPreviousTokens={setPreviousTokens}
                                        nextToken={nextToken}
                                        />
                                    <TerminalInfoTable terminalInfo={terminalInfo} />
                                    
                                </AccordionDetails>
                            </Accordion>

                             {/* Log Session */}
                            <Accordion>
                                <AccordionSummary
                                style={{width: '100%', padding: '0 16px'}}
                                expandIcon={<ExpandMoreIcon/>}
                                >
                                <Typography variant="h5">Log</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                <TableContainer component={Paper}>
                                    <Table sx={{ minWidth: 650 }} aria-label="log table">
                                    <TableBody>
                                        {(() => {
                                            try {
                                                const logData = log ? JSON.parse(log) : null;
                                                if (!logData || Object.keys(logData).length === 0) {
                                                return (
                                                    <TableRow>
                                                    <TableCell colSpan={2} align="center">
                                                        No log data available
                                                    </TableCell>
                                                    </TableRow>
                                                );
                                                }
                                                return Object.keys(logData).map((key) => (
                                                <TableRow key={key}>
                                                    <TableCell component="th" scope="row" style={{
                                                    borderRight: "0.5px solid #DEDEDE"
                                                    }}>
                                                    {key}
                                                    </TableCell>
                                                    <TableCell align="left">
                                                    {logData[key] && (logData[key].startsWith('http') || logData[key].startsWith('s3://')) ? (
                                                        <a href={logData[key]} target="_blank" rel="noopener noreferrer">
                                                        {logData[key]}
                                                        </a>
                                                    ) : (
                                                        logData[key]
                                                    )}
                                                    </TableCell>
                                                </TableRow>
                                                ));
                                            } catch (e) {
                                                return (
                                                <TableRow>
                                                    <TableCell colSpan={2} align="center" style={{ color: 'red' }}>
                                                    Error parsing log data
                                                    </TableCell>
                                                </TableRow>
                                                );
                                            }
                                            })()}
                                    </TableBody>
                                    </Table>
                                </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        </>     
                    }
                </Box>
            </Box>
            <Button variant="contained" style={{marginTop: '30px'}} onClick={onClickBackBtn}>Back</Button>
            <Backdrop open={open} className={classes.backdrop} >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    )
}

export default RouterSettingPage;